/**
 * Pure, store-free FX chain helpers shared by:
 * - the realtime Web Audio graph (AudioService, desktop)
 * - the offline renderer (OfflineFxService, iOS)
 *
 * Keeping the graph construction and gain math in one place guarantees the
 * offline render sounds identical to the realtime chain.
 */

/**
 * Convert decibels to linear gain.
 * @param {number} db
 * @returns {number}
 */
export function dbToGain(db) {
  return Math.pow(10, db / 20);
}

/**
 * Estimate the automatic makeup gain WebAudio's DynamicsCompressor applies,
 * so it can be compensated away when the user disables auto makeup.
 * Mirrors the previous AudioService.calculateAutoMakeupGain, minus the store read.
 *
 * @param {number} threshold - dB (negative)
 * @param {number} ratio
 * @param {boolean} autoMakeupEnabled - when true, keep built-in behavior (no compensation)
 * @returns {number} estimated auto-makeup in dB
 */
export function calculateAutoMakeupGain(threshold, ratio, autoMakeupEnabled) {
  if (autoMakeupEnabled) return 0;
  if (ratio <= 1) return 0;

  const fullScaleReductionDb = -threshold * (1 - 1 / ratio);
  const compensationFactor = 0.27; // empirically tuned (see COMPRESSOR_MAKEUP_GAIN_COMPENSATION.md)
  return fullScaleReductionDb * compensationFactor;
}

/**
 * Compute the compensated makeup gain (dB) to set on the makeup GainNode.
 * @param {{threshold:number, ratio:number, makeupGain:number}} compressorSettings
 * @param {boolean} autoMakeupEnabled
 * @returns {number} dB
 */
export function computeCompensatedMakeupGainDb(compressorSettings, autoMakeupEnabled) {
  const autoMakeup = calculateAutoMakeupGain(
    compressorSettings.threshold,
    compressorSettings.ratio,
    autoMakeupEnabled
  );
  return compressorSettings.makeupGain - autoMakeup;
}

/**
 * Build the FX node chain on any BaseAudioContext (realtime or offline):
 *
 *   input(eq highShelf) -> eq mid3 -> eq mid2 -> eq mid1 -> eq lowShelf
 *     -> compressor -> makeup gain -> output
 *
 * Disabled EQ renders flat (0 dB gains); disabled compressor renders 1:1 with
 * unity makeup gain — identical to the realtime bypass behavior.
 *
 * @param {BaseAudioContext} ctx
 * @param {object} settings
 * @param {object} settings.eqSettings - { highShelf, mid3, mid2, mid1, lowShelf } each { frequency, gain, q }
 * @param {object} settings.compressorSettings - { threshold, ratio, attack, release, makeupGain }
 * @param {boolean} settings.eqEnabled
 * @param {boolean} settings.compressorEnabled
 * @param {boolean} settings.autoMakeupEnabled
 * @returns {{ input: AudioNode, output: AudioNode, eqFilters: BiquadFilterNode[], compressor: DynamicsCompressorNode, gainNode: GainNode }}
 */
export function buildFxChain(ctx, settings) {
  const {
    eqSettings,
    compressorSettings,
    eqEnabled,
    compressorEnabled,
    autoMakeupEnabled
  } = settings;

  const makeFilter = (type, band) => {
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = band.frequency;
    filter.gain.value = eqEnabled ? band.gain : 0;
    filter.Q.value = band.q;
    return filter;
  };

  // Order matches the realtime graph: highShelf, mid3, mid2, mid1, lowShelf
  const eqFilters = [
    makeFilter('highshelf', eqSettings.highShelf),
    makeFilter('peaking', eqSettings.mid3),
    makeFilter('peaking', eqSettings.mid2),
    makeFilter('peaking', eqSettings.mid1),
    makeFilter('lowshelf', eqSettings.lowShelf)
  ];

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = compressorSettings.threshold;
  compressor.ratio.value = compressorEnabled ? compressorSettings.ratio : 1;
  compressor.attack.value = compressorSettings.attack / 1000; // ms -> s
  compressor.release.value = compressorSettings.release / 1000; // ms -> s

  const gainNode = ctx.createGain();
  gainNode.gain.value = compressorEnabled
    ? dbToGain(computeCompensatedMakeupGainDb(compressorSettings, autoMakeupEnabled))
    : 1;

  for (let i = 0; i < eqFilters.length - 1; i++) {
    eqFilters[i].connect(eqFilters[i + 1]);
  }
  eqFilters[eqFilters.length - 1].connect(compressor);
  compressor.connect(gainNode);

  return {
    input: eqFilters[0],
    output: gainNode,
    eqFilters,
    compressor,
    gainNode
  };
}
